import { ActionIcon, Badge, Box, Group, Modal, Text } from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconSparkles,
  IconX,
} from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { barve } from '../tema';
import { slikaUrl } from './javno';
import { usePoteg } from './poteg';

/** Čas ene slike pri samodejnem predvajanju. */
const INTERVAL_MS = 4000;

/**
 * Celozaslonski ogled slik vozila: velika slika, puščice, tipke ← → (preslednica = predvajaj/ustavi, Esc zapre),
 * poteg s prstom, samodejno predvajanje in sličice na dnu. Kjer brskalnik dovoli, preklopi tudi v pravi celozaslonski
 * način (na iPhonu in v vgrajeni strani brez dovoljenja ostane okno čez celo stran).
 */
export function Diaprojekcija({
  odprta,
  zapri,
  oznaka,
  slike,
  slikeAi,
  naziv,
  izbrana,
  izberi,
}: {
  odprta: boolean;
  zapri: () => void;
  oznaka: string;
  slike: number[];
  slikeAi: number[];
  naziv: string;
  izbrana: number;
  izberi: (i: number) => void;
}) {
  const [predvaja, setPredvaja] = useState(false);
  const sklici = useRef<(HTMLButtonElement | null)[]>([]);
  const vec = slike.length > 1;
  const pojdi = (smer: number) => izberi((izbrana + smer + slike.length) % slike.length);
  const poteg = usePoteg(pojdi);

  // tipke; pojdi se spremeni z vsako sliko, zato poslušalca zamenjamo
  useEffect(() => {
    if (!odprta) {
      return;
    }
    const tipka = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        pojdi(1);
      } else if (e.key === 'ArrowLeft') {
        pojdi(-1);
      } else if (e.key === ' ' && vec) {
        e.preventDefault();
        setPredvaja((p) => !p);
      }
    };
    window.addEventListener('keydown', tipka);
    return () => window.removeEventListener('keydown', tipka);
  });

  // samodejno predvajanje; ročni premik začne odštevanje znova
  useEffect(() => {
    if (!odprta || !predvaja || !vec) {
      return;
    }
    const t = window.setTimeout(() => izberi((izbrana + 1) % slike.length), INTERVAL_MS);
    return () => window.clearTimeout(t);
  }, [odprta, predvaja, vec, izbrana, slike.length, izberi]);

  useEffect(() => {
    sklici.current[izbrana]?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [izbrana, odprta]);

  // pravi celozaslonski način, kjer ga brskalnik podpira; ob zaprtju ga zapustimo
  useEffect(() => {
    if (!odprta) {
      return;
    }
    if (!document.fullscreenElement && document.fullscreenEnabled) {
      document.documentElement.requestFullscreen?.().catch(() => undefined);
    }
    return () => {
      setPredvaja(false);
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => undefined);
      }
    };
  }, [odprta]);

  const id = slike[izbrana];

  // sosednji sliki naložimo vnaprej, da je premik takojšen
  useEffect(() => {
    if (!odprta || !vec) {
      return;
    }
    for (const s of [slike[(izbrana + 1) % slike.length], slike[(izbrana - 1 + slike.length) % slike.length]]) {
      if (s !== undefined) {
        new Image().src = slikaUrl(oznaka, s);
      }
    }
  }, [odprta, vec, izbrana, slike, oznaka]);

  return (
    <Modal
      opened={odprta}
      onClose={zapri}
      fullScreen
      withCloseButton={false}
      padding={0}
      transitionProps={{ transition: 'fade', duration: 150 }}
      styles={{
        content: { background: barve.ozadjeTemno, color: 'white' },
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm" px="md" py="xs" style={{ flexShrink: 0 }}>
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <Text fw={700} style={{ flexShrink: 0 }}>
            {izbrana + 1} / {slike.length}
          </Text>
          <Text c={barve.besediloNaOzadjuSekundarno} truncate visibleFrom="xs">
            {naziv}
          </Text>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {vec && (
            <ActionIcon
              variant="subtle"
              color="gray.0"
              size="xl"
              radius="xl"
              aria-label={predvaja ? 'Ustavi predvajanje' : 'Predvajaj'}
              onClick={() => setPredvaja((p) => !p)}
            >
              {predvaja ? <IconPlayerPauseFilled size={22} /> : <IconPlayerPlayFilled size={22} />}
            </ActionIcon>
          )}
          <ActionIcon variant="subtle" color="gray.0" size="xl" radius="xl" aria-label="Zapri" onClick={zapri}>
            <IconX size={26} />
          </ActionIcon>
        </Group>
      </Group>

      <Box pos="relative" style={{ flex: 1, minHeight: 0, touchAction: 'pan-y' }} {...poteg}>
        {id !== undefined && (
          <img
            key={id}
            src={slikaUrl(oznaka, id)}
            alt={`${naziv} – slika ${izbrana + 1} od ${slike.length}`}
            draggable={false}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              userSelect: 'none',
            }}
          />
        )}
        {vec && (
          <>
            <ActionIcon
              variant="white"
              radius="xl"
              size="xl"
              aria-label="Prejšnja slika"
              onClick={() => pojdi(-1)}
              style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', opacity: 0.9 }}
            >
              <IconChevronLeft />
            </ActionIcon>
            <ActionIcon
              variant="white"
              radius="xl"
              size="xl"
              aria-label="Naslednja slika"
              onClick={() => pojdi(1)}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', opacity: 0.9 }}
            >
              <IconChevronRight />
            </ActionIcon>
          </>
        )}
        {id !== undefined && slikeAi.includes(id) && (
          <Badge
            variant="filled"
            color="dark"
            leftSection={<IconSparkles size={12} />}
            style={{ position: 'absolute', left: 12, bottom: 12, opacity: 0.8, textTransform: 'none' }}
          >
            Urejeno z AI
          </Badge>
        )}
      </Box>

      {vec && (
        <Group
          gap={6}
          wrap="nowrap"
          px="md"
          py="sm"
          style={{ flexShrink: 0, overflowX: 'auto', justifyContent: 'safe center' }}
        >
          {slike.map((s, i) => (
            <Box
              key={s}
              ref={(el: HTMLButtonElement | null) => {
                sklici.current[i] = el;
              }}
              component="button"
              type="button"
              onClick={() => izberi(i)}
              aria-label={`Slika ${i + 1}`}
              aria-current={i === izbrana}
              p={0}
              style={{
                border: `3px solid ${i === izbrana ? barve.poudarek : 'transparent'}`,
                borderRadius: 8,
                overflow: 'hidden',
                flexShrink: 0,
                width: 72,
                height: 54,
                cursor: 'pointer',
                background: barve.ozadje,
                opacity: i === izbrana ? 1 : 0.6,
              }}
            >
              <img
                src={slikaUrl(oznaka, s, true)}
                alt=""
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          ))}
        </Group>
      )}
    </Modal>
  );
}
